// Carte « Dossiers passés dûs » (demande du propriétaire, 2026-10-27, onglet
// Rapports) : dossiers dont l'ENTENTE de paiement (COD, 15 jours, 30 jours)
// est dépassée + TOTAL EN SOUFFRANCE. Lecture seule — la relance part toute
// seule (réglable dans Paramètres → Relances) ou via la pilule
// « Dû / passé dû » de la fiche.
import { useEffect, useState } from "react";
import { AlarmClock, FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export default function RapportRetards() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const charger = () => {
    setBusy(true);
    api.get("/admin/rapports/passes-dus")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { charger(); }, []);
  const dossiers = data?.dossiers || [];
  return (
    <div className="bg-surface border border-ivory/10 rounded-xl p-6" data-testid="rapport-retards">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-semibold flex items-center gap-2">
            <AlarmClock className="w-4 h-4 text-red-400" /> Dossiers passés dûs
          </p>
          <p className="text-xs text-ivory/70 mt-1 max-w-xl">
            Factures dont l'entente de paiement (COD, 15 jours, 30 jours) est dépassée.
            La relance part toute seule (+3 j après l'échéance, puis aux 2 j — réglable
            dans Paramètres → Relances) ; le détail se voit sur la carte de vente
            (pastille rouge « Passé dû de N j »).
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-widest text-ivory/50">Total en souffrance</p>
            <p className={`font-serif text-3xl ${data && data.total > 0 ? "text-red-400" : "text-emerald-300"}`}
              data-testid="rapport-retards-total">
              {busy ? "…" : `${(data?.total || 0).toFixed(2)} $`}
            </p>
          </div>
          <button type="button" onClick={charger} disabled={busy} data-testid="rapport-retards-refresh"
            title="Recharger la liste"
            className="inline-flex items-center gap-1.5 border border-ivory/20 text-ivory/70 px-3.5 py-2 rounded-full text-xs hover:border-gold hover:text-gold transition-colors duration-300 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Actualiser
          </button>
          {/* PDF pour la comptable (demande du propriétaire, 2026-10-27) :
              la liste des passés dûs + total en souffrance, en un fichier */}
          <button type="button" disabled={busy} data-testid="rapport-retards-pdf"
            title="Télécharger la liste des dossiers passés dûs en PDF (pour la comptable)"
            onClick={async () => {
              try {
                const resp = await api.get("/admin/rapports/passes-dus.pdf", { responseType: "blob" });
                window.open(URL.createObjectURL(resp.data), "_blank", "noopener");
              } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
            }}
            className="inline-flex items-center gap-1.5 border border-gold/50 text-gold px-3.5 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50">
            <FileDown className="w-3.5 h-3.5" /> PDF pour la comptable
          </button>
        </div>
      </div>
      {!busy && dossiers.length === 0 && (
        <p className="text-sm text-emerald-300 mt-4" data-testid="rapport-retards-vide">
          Aucun dossier passé dû — tout est à jour.
        </p>
      )}
      {dossiers.length > 0 && (
        <div className="mt-4 divide-y divide-ivory/8" data-testid="rapport-retards-liste">
          {dossiers.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3"
              data-testid={`rapport-retard-${d.id}`}>
              <div className="min-w-40">
                <p className="text-sm font-semibold truncate">{d.name}</p>
                <p className="text-[11px] text-ivory/50 truncate">{d.email}{d.phone ? ` — ${d.phone}` : ""}</p>
              </div>
              <span className="text-[10px] uppercase tracking-widest text-ivory/50">
                {d.terme} — échéance {d.echeance}
              </span>
              <span className="text-[10px] uppercase tracking-widest border border-red-500 bg-red-500/20 text-red-300 font-bold rounded-full px-2 py-0.5"
                data-testid={`rapport-retard-badge-${d.id}`}>
                Passé dû de {d.retard} j
              </span>
              {d.relances > 0 && (
                <span className="text-[10px] text-amber-300/80" title="Relances automatiques déjà envoyées">
                  {d.relances} relance{d.relances > 1 ? "s" : ""}
                </span>
              )}
              <span className="ml-auto font-serif text-lg text-red-300" data-testid={`rapport-retard-montant-${d.id}`}>
                {d.montant.toFixed(2)} $
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
